import { useEffect, useRef, useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { css, cx, getTextStyles } from '@eti/styles';
import { m as motion, AnimatePresence } from 'framer-motion';
import { DangerousHtmlContent } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { breakpoints, Text, textVariants, vars } from '@etg/wings';
import { TRACKING_PROPERTIES, useProductSnowplowTracking } from '../../custom-hooks/useProductSnowplowTracking';
import { DYNAMIC_ICON } from '../utils/dynamic-icons/icons';
import { useDynamicIcon } from '../utils/dynamic-icons/useDynamicIcon';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const wrapperStyles = css`
  border-block: 1px solid ${vars.colors.divider};
  display: flex;
  flex-flow: column wrap;
  grid-column: 2;
  grid-row: 2;
  padding: 0;

  ul > *:not(:last-child) {
    margin-bottom: 8px;
  }

  p ~ a {
    display: block;
  }
`;
const clickableArea = css`
  align-items: center;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex: 1;
  justify-content: space-between;
  padding: 16px;
  z-index: 1;
`;
const fontSize = css`
  & li:not(:last-child) {
    margin-bottom: 8px;
  }

  @media (min-width: ${breakpoints._768}) {
    font-size: 0.875rem;
  }
`;
const toggleIconStyles = open => css`
  transform: rotateX(${open ? '180deg' : '0deg'});
  transition: transform 0.2s ease-in-out;
`;
const contentText = iconContent => css`
  margin-bottom: 16px;
  padding-inline: 24px 16px;

  > *:not(:last-child) {
    &:not(a) {
      margin-bottom: 16px;
    }
  }

  ul {
    li {
      display: flex;
      padding-inline-start: 12px;
      &::before {
        display: flex;
        left: 0;
        margin-right: 8px;
        position: absolute;
      }
    }
  }

  a {
    display: block;
    position: relative;
    width: fit-content;

    + p {
      margin-top: 16px;
    }

    &::after {
      content: ${iconContent !== null && iconContent !== void 0 ? iconContent : ''};
      margin-inline-start: 2px;
      position: absolute;
      top: -1px;
    }
  }
`;
const readMoreTextStyles = linkIconContent => css`
  ${contentText(linkIconContent)};
`;
const ProductReadMore = ({
  content,
  productName
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [icon] = useDynamicIcon(vars.colors.links.default, DYNAMIC_ICON.linkIcon);
  const rootElement = useRef(null);
  const {
    sendClickEvent,
    addTrackingEventOnLinks
  } = useProductSnowplowTracking(productName, {
    rootElement,
    sendSeenEvent: false
  });
  const {
    t
  } = useTranslation();
  const handleToggle = () => {
    setIsOpen(prevIsOpen => !prevIsOpen);
  };
  useEffect(() => {
    if (isOpen) {
      addTrackingEventOnLinks(rootElement);
      sendClickEvent({
        property: TRACKING_PROPERTIES.READ_MORE
      });
    }

    // oxlint-disable-next-line react/exhaustive-deps
  }, [isOpen]);
  return /*#__PURE__*/_jsxs("div", {
    className: wrapperStyles,
    "data-abtest": "new-readMore-design",
    "data-testid": `${productName}-readmore-container`,
    ref: rootElement,
    children: [/*#__PURE__*/_jsxs("button", {
      "aria-controls": `${productName}-readMoreContent`,
      "aria-expanded": isOpen,
      className: cx(getTextStyles(), fontSize, clickableArea),
      "data-testid": `${productName}-readMoreLink`,
      onClick: handleToggle,
      type: "button",
      children: [/*#__PURE__*/_jsx(Text, {
        variant: textVariants._14_BOLD,
        children: t('Products.ReadMore.Link.Label')
      }), /*#__PURE__*/_jsx(CaretDown, {
        className: toggleIconStyles(isOpen),
        color: "#262626",
        size: 24,
        weight: "light"
      })]
    }), /*#__PURE__*/_jsx(AnimatePresence, {
      children: isOpen && /*#__PURE__*/_jsx(motion.div, {
        animate: {
          height: 'auto'
        },
        "aria-hidden": isOpen ? 'false' : 'true',
        "data-testid": "motion-component",
        exit: {
          height: 0
        },
        initial: {
          height: 0,
          overflow: 'hidden'
        },
        style: {
          overflow: 'hidden'
        },
        transition: {
          duration: 0.3
        },
        children: /*#__PURE__*/_jsx(DangerousHtmlContent, {
          className: cx(getTextStyles(), fontSize, readMoreTextStyles(icon)),
          content: content,
          id: `${productName}-readMoreContent`
        })
      })
    })]
  });
};
export default ProductReadMore;
//# sourceMappingURL=ProductReadMore.js.map